"use client";

import type { PlaceholderValue } from "@homeinstead/schemas";
import type { PortableTextComponents } from "@portabletext/react";
import type { PortableTextBlock } from "@portabletext/types";
import type { HeadingFont, RichTextComponentsFactory } from "./types";

import { PortableText } from "@portabletext/react";
import { useMemo } from "react";
import { cn } from "../../utils/classnames";
import { Grid } from "../Grid";
import { clampListItemLevels } from "./clamp-list-item-levels";
import { RICH_TEXT_CONTAINER_WIDTH_CLASS } from "./has-container-width-rich-text-block";

type RichTextColumn = {
  _key: string;
  content?: PortableTextBlock[];
};

type RichTextColumnsValue = {
  columnCount?: 1 | 2 | 3 | 4;
  columns?: RichTextColumn[];
  width?: "full" | "prose";
};

type RichTextColumnsProps = {
  activePlaceholderKeys: readonly string[];
  components?: Partial<PortableTextComponents>;
  createRichTextComponents: RichTextComponentsFactory;
  headingFont?: HeadingFont;
  onLinkClick?: (href: string) => void;
  placeholders: Record<string, PlaceholderValue>;
  value: RichTextColumnsValue;
};

function RichTextColumns({
  activePlaceholderKeys,
  components,
  createRichTextComponents,
  headingFont,
  onLinkClick,
  placeholders,
  value: { columnCount, columns, width },
}: RichTextColumnsProps) {
  const cellComponents = useMemo(
    () =>
      createRichTextComponents({
        activePlaceholderKeys,
        components,
        headingFont,
        includeAccordion: false,
        includeColumns: false,
        onLinkClick,
        placeholders,
      }),
    [
      activePlaceholderKeys,
      components,
      createRichTextComponents,
      headingFont,
      onLinkClick,
      placeholders,
    ],
  );

  if (!Array.isArray(columns) || columns.length < 1) {
    return null;
  }

  return (
    <Grid
      as="div"
      className={cn(
        "my-6",
        width === "prose" ? undefined : RICH_TEXT_CONTAINER_WIDTH_CLASS,
      )}
      columnCount={columnCount}
    >
      {columns.map((column) => (
        <div key={column._key}>
          <PortableText
            components={cellComponents}
            value={clampListItemLevels(column.content ?? [])}
          />
        </div>
      ))}
    </Grid>
  );
}

export { RichTextColumns };
